import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных рекламных кампаний
 */
const ComparisonSidebar = ({ 
  savedCampaigns, 
  deleteSavedCampaign, 
  loadSavedCampaign 
}) => {
  // Если нет сохраненных кампаний
  if (!savedCampaigns || savedCampaigns.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение кампаний
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты рекламных кампаний для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение кампаний
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCampaigns.length} {pluralize(savedCampaigns.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCampaigns.map((campaign) => (
          <div 
            key={campaign.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {campaign.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCampaign(campaign.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCampaign(campaign.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Бюджет:</span>
                <span className="font-medium">
                  {formatCurrency(campaign.results.campaign.totalBudget, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Длительность:</span>
                <span>
                  {campaign.results.campaign.campaignDuration} {pluralize(campaign.results.campaign.campaignDuration, 'день', 'дня', 'дней')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Конверсии:</span>
                <span>
                  {formatNumber(campaign.results.performance.totalConversions, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Эффективность:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {campaign.results.effectiveness.score}/100
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;